import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message, Spin } from 'antd';
import { pdfApi } from '../../services/api';
import PDFContentWorkspace from '../../components/PDFContentEditor/PDFContentWorkspace';
import { useAuth } from '../../contexts/AuthContext';

const Editor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [initialContent, setInitialContent] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [messageApi, contextHolder] = message.useMessage();
  const workspaceRef = useRef(null);
  const lastSavedContentRef = useRef('');
  const isMountedRef = useRef(true);

  // 组件挂载状态跟踪
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 获取文件信息
  useEffect(() => {
    const fetchFile = async () => {
      try {
        // 检查是否有token
        const token = localStorage.getItem('token');
        if (!token) {
          messageApi.error('请先登录');
          navigate('/login');
          return;
        }

        const data = await pdfApi.getFile(id);
        console.log('pdfApi.getFile(id)返回:', data);
        if (!isMountedRef.current) return;
        
        setFile(data.data ? data.data : data);
        const extractResult = (data.data ? data.data.extract_result : data.extract_result) || '';
        console.log('extractResult:', extractResult);
        setInitialContent(extractResult);
        lastSavedContentRef.current = extractResult;
        
        // 获取文件的签名URL
        try {
          const urlData = await pdfApi.getSignedUrl(id);
          if (!isMountedRef.current) return;
          
          setFileUrl(urlData.data.signed_url);
          console.log("获取到签名URL:", urlData.data.signed_url);
        } catch (urlErr) {
          if (!isMountedRef.current) return;
          
          console.error('获取签名URL失败:', urlErr);
          if (urlErr.response?.status === 401) {
            messageApi.error('登录已过期，请重新登录');
            logout();
            navigate('/login');
          } else {
            messageApi.error('获取文件URL失败：' + (urlErr.response?.data?.detail || urlErr.message));
            navigate('/pdf/files');
          }
        }
      } catch (err) {
        if (!isMountedRef.current) return;
        
        console.error('获取文件信息失败:', err);
        if (err.response?.status === 401) {
          messageApi.error('登录已过期，请重新登录');
          logout();
          navigate('/login');
        } else {
          messageApi.error('获取文件信息失败：' + (err.response?.data?.detail || err.message));
          navigate('/pdf/files');
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchFile();
  }, [id, navigate, logout, messageApi]);

  // 处理内容保存
  const handleSave = useCallback(async (content) => {
    // 避免保存空内容或无效内容
    if (!file || !content || content === lastSavedContentRef.current || !isMountedRef.current) {
      return;
    }
    
    try {
      const response = await pdfApi.updateContent(file.id, content);
      
      if (!isMountedRef.current) return;
      
      lastSavedContentRef.current = content;
      
      // 显示保存成功信息，包括已删除的图片数量
      if (response.deleted_images_count > 0) {
        messageApi.success(`内容已保存，${response.deleted_images_count}张不再使用的图片已被删除`);
      } else {
        messageApi.success('内容已保存');
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      
      console.error('保存内容失败:', err);
      if (err.response?.status === 401) {
        messageApi.error('登录已过期，请重新登录');
        logout();
        navigate('/login');
      } else {
        messageApi.error('保存失败：' + (err.response?.data?.detail || err.message));
      }
    }
  }, [file, logout, messageApi, navigate]);

  // 离开页面前保存
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (workspaceRef.current && isMountedRef.current) {
        try {
          const content = workspaceRef.current.getContent();
          if (content && content !== lastSavedContentRef.current) {
            // 仅显示标准的"离开页面前保存"提示
            e.preventDefault();
            e.returnValue = '';
          }
        } catch (error) {
          console.error('离开页面获取内容失败:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  if (loading || !file || !file.id) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!fileUrl) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>无法加载PDF文件</div>
        <div style={{ marginTop: '20px' }}>
          <button onClick={() => navigate('/pdf/files')}>返回文件列表</button>
        </div>
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <div style={{ 
        position: 'relative',
        height: 'calc(100vh - 64px)', // 设置明确的高度，减去头部导航栏的高度
        width: '100%',
        overflow: 'hidden'
      }}>
        <PDFContentWorkspace
          ref={workspaceRef}
          file={fileUrl}
          fileId={file.id}
          content={initialContent}
          onSave={handleSave}
        />
      </div>
    </>
  );
};

export default Editor; 